import React, { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Header, Footer } from "../components/Chrome";
import { useSettings } from "../lib/settings";
import { resolveImage } from "../lib/api";
import { useLiveFetch } from "../lib/useLiveFetch";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import { toast } from "sonner";

function ProductCard({ p, catName, i, canOrder, onAdd }) {
  const [qty, setQty] = useState(p.min_order_qty || 1);
  const soldOut = p.availability === "out_of_stock" || p.is_coming_soon;
  // Payment badge — normalise legacy values so historical data still renders.
  const pmRaw = p.payment_method;
  const pm = pmRaw === "cash" || pmRaw === "cash_only" ? "cash" : "electronic_or_cheque";
  const pmBadge = pm === "cash"
    ? { label: "Cash only", className: "bg-soda text-paper border-soda" }
    : { label: "E-Transfer / Cheque", className: "bg-paper text-ink border-ink" };
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }} transition={{ duration: 0.5, delay: (i % 12) * 0.03 }}
      className="border border-ink bg-paper group hover:hard-shadow relative flex flex-col"
      style={{ transition: "box-shadow 250ms ease" }}
      data-testid={`catalog-item-${p.id}`}
    >
      <div className="aspect-square overflow-hidden border-b border-ink relative">
        {p.image_url ? (
          <img src={resolveImage(p.image_url)} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
        ) : (
          <div className="w-full h-full bg-secondary flex items-center justify-center overline text-black/40">No image</div>
        )}
        {p.is_coming_soon && (
          <div className="absolute top-3 right-3 bg-soda text-paper px-3 py-1 text-[10px] font-bold uppercase tracking-widest" data-testid={`badge-coming-${p.id}`}>Coming soon</div>
        )}
        {p.is_featured && !p.is_coming_soon && (
          <div className="absolute top-3 left-3 bg-ink text-paper px-3 py-1 text-[10px] font-bold uppercase tracking-widest">Featured</div>
        )}
        {p.availability === "out_of_stock" && (
          <div className="absolute bottom-0 inset-x-0 bg-ink/80 text-paper text-center py-1 text-[10px] font-bold uppercase tracking-widest">Out of stock</div>
        )}
      </div>
      <div className="p-3 md:p-4 flex-1 flex flex-col">
        <div className="overline text-black/50 mb-2 flex items-center gap-2">
          {catName}{p.is_imported && <span className="text-soda">★</span>}
        </div>
        <div className="font-bold text-sm md:text-base leading-tight line-clamp-2">{p.name}</div>
        {p.brand && <div className="text-xs text-black/60 mt-1">{p.brand}</div>}
        {(p.unit_size || p.case_size) && (
          <div className="text-xs text-black/60 mt-2 space-y-0.5" data-testid={`size-${p.id}`}>
            {p.unit_size && <div><span className="text-black/40">Unit:</span> {p.unit_size} per unit</div>}
            {p.case_size && <div><span className="text-black/40">Case:</span> {p.case_size} per case</div>}
          </div>
        )}
        {p.description && <p className="text-xs text-black/60 mt-2 line-clamp-2">{p.description}</p>}
        <div className="mt-3">
          <span
            className={`inline-block border px-2 py-0.5 text-[9px] md:text-[10px] font-bold uppercase tracking-widest ${pmBadge.className}`}
            data-testid={`payment-badge-${p.id}`}
            title={pm === "cash" ? "Payable only in cash on delivery" : "Payable by Interac e-transfer or cheque"}
          >
            {pmBadge.label}
          </span>
        </div>
        {canOrder && (
          <div className="mt-3 pt-3 border-t border-black/10">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-black/60">Wholesale</div>
              <div className="font-bold text-sm">${(Number(p.wholesale_price) || 0).toFixed(2)}</div>
            </div>
            {!soldOut ? (
              <div className="flex gap-2">
                <input type="number" min={p.min_order_qty || 1} value={qty}
                       onChange={e => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                       className="field text-sm py-1 w-16" data-testid={`qty-${p.id}`} />
                <button onClick={() => onAdd(p, qty)} className="btn-primary text-[11px] px-3 py-1 flex-1"
                        data-testid={`add-to-cart-${p.id}`}>Add to cart</button>
              </div>
            ) : (
              <div className="text-[11px] text-black/50 italic">Not available for order</div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function CatalogPublic() {
  const { data: itemsData, loading: loadingItems, error: itemsError, reload: reloadItems } = useLiveFetch("/products/public", { initial: [] });
  const { data: catsData } = useLiveFetch("/categories/public", { initial: [] });
  const { data: brandsData } = useLiveFetch("/brands/public", { initial: [] });
  const items = itemsData || [];
  const cats = catsData || [];
  const brands = brandsData || [];
  const [params, setParams] = useSearchParams();
  const { settings } = useSettings();
  const { user } = useAuth();
  const { add } = useCart();
  const canOrder = !!(settings?.ordering_enabled && user && user.role === "retailer" && user.status === "approved");
  const onAdd = (p, qty) => {
    add(p, qty);
    toast.success(`Added ${qty} × ${p.name} to cart`);
  };

  const sectionParam = params.get("section") || "all";
  const activeCat = params.get("category") || "all";
  const activeBrand = params.get("brand") || "all";
  // Coming-soon-only view: `?coming=1` (or ?coming=true) shows only products
  // flagged is_coming_soon OR products inside a coming_soon category. Wired to
  // the footer "Coming soon →" link.
  const comingOnly = ["1", "true", "yes"].includes((params.get("coming") || "").toLowerCase());

  const catBySlug = useMemo(() => Object.fromEntries(cats.map(c => [c.slug, c])), [cats]);
  const brandByName = useMemo(() => Object.fromEntries(brands.map(b => [b.name, b])), [brands]);

  const visibleCats = useMemo(() => {
    let list = cats;
    if (sectionParam === "regular") list = list.filter(c => c.section === "regular");
    if (sectionParam === "imported") list = list.filter(c => c.section === "imported");
    if (comingOnly) list = list.filter(c => c.status === "coming_soon");
    return list;
  }, [cats, sectionParam, comingOnly]);

  const filtered = useMemo(() => {
    return items.filter(p => {
      const c = catBySlug[p.category_slug];
      if (!c || c.status === "hidden") return false;
      if (sectionParam === "regular" && c.section !== "regular") return false;
      if (sectionParam === "imported" && c.section !== "imported") return false;
      if (activeCat !== "all" && p.category_slug !== activeCat) return false;
      if (activeBrand !== "all" && (p.brand || "") !== activeBrand) return false;
      if (comingOnly && !p.is_coming_soon && c.status !== "coming_soon") return false;
      return true;
    });
  }, [items, catBySlug, sectionParam, activeCat, activeBrand, comingOnly]);

  // Group filtered products by brand (used when a single category is active)
  const groupedByBrand = useMemo(() => {
    const groups = {};
    filtered.forEach(p => {
      const key = p.brand || "__nobrand__";
      (groups[key] = groups[key] || []).push(p);
    });
    // order: brands by display_order, no-brand last
    const keys = Object.keys(groups).sort((a, b) => {
      if (a === "__nobrand__") return 1;
      if (b === "__nobrand__") return -1;
      return (brandByName[a]?.display_order ?? 999) - (brandByName[b]?.display_order ?? 999);
    });
    return keys.map(k => ({ key: k, brand: brandByName[k], products: groups[k] }));
  }, [filtered, brandByName]);

  const setParam = (key, val) => {
    const p = new URLSearchParams(params);
    if (val === "all" || !val) p.delete(key); else p.set(key, val);
    if (key === "category") p.delete("brand");
    setParams(p, { replace: true });
  };

  const activeCatObj = catBySlug[activeCat];
  const showBrandGroups = activeCat !== "all" && activeBrand === "all";

  return (
    <div className="min-h-screen bg-paper text-ink">
      <Header />
      <section className="pt-28 md:pt-32 pb-10 md:pb-14 px-6 md:px-12 lg:px-16 border-b border-ink" data-testid="catalog-hero">
        <div className="overline text-black/50 mb-6">Product Catalog</div>
        <h1 className="font-display text-[13vw] md:text-[10vw] lg:text-[9vw] leading-[0.85] uppercase tracking-tighter">
          {comingOnly
            ? <>Coming <em className="font-editorial italic tracking-normal">soon.</em></>
            : sectionParam === "imported"
              ? <>Imported <em className="font-editorial italic tracking-normal">reserve.</em></>
              : <>The <em className="font-editorial italic tracking-normal">whole</em> shelf.</>}
        </h1>
        <div className="mt-8 md:mt-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <p className="md:col-span-7 text-lg md:text-xl leading-relaxed">
            {comingOnly
              ? "New categories and products we're bringing in soon. Send an enquiry and we'll notify you the moment they arrive."
              : "Preview of everything we distribute. Wholesale pricing and availability provided by request — send an enquiry and a team member will follow up within one business day."}
          </p>
          <div className="md:col-span-4 md:col-start-9 flex flex-wrap gap-3">
            {comingOnly && <Link to="/catalog" className="btn-ghost" data-testid="clear-coming-filter">← Back to full catalog</Link>}
            {settings?.enquiry_form_enabled && <Link to="/enquiry" className="btn-primary" data-testid="catalog-enquiry-btn">Request pricing →</Link>}
            {!comingOnly && <Link to="/contact" className="btn-ghost" data-testid="catalog-contact-btn">Contact us</Link>}
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-16 pt-6 md:pt-8">
        <div className="flex gap-2 mb-4">
          <button onClick={() => setParam("section", "all")} className={`chip ${sectionParam === "all" ? "bg-ink text-paper" : ""}`} data-testid="section-all">All</button>
          <button onClick={() => setParam("section", "regular")} className={`chip ${sectionParam === "regular" ? "bg-ink text-paper" : ""}`} data-testid="section-regular">Regular Catalog</button>
          <button onClick={() => setParam("section", "imported")} className={`chip ${sectionParam === "imported" ? "bg-ink text-paper" : ""}`} data-testid="section-imported">Imported</button>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-16 pb-4 sticky top-16 md:top-20 bg-paper z-30 border-b border-black/10">
        <div className="flex flex-wrap gap-2 py-3" data-testid="catalog-filters">
          <button onClick={() => setParam("category", "all")} className={`chip ${activeCat === "all" ? "bg-ink text-paper" : ""}`} data-testid="filter-all">All ({filtered.length})</button>
          {visibleCats.map(c => (
            <button key={c.id} onClick={() => setParam("category", c.slug)}
              className={`chip ${activeCat === c.slug ? "bg-ink text-paper" : ""} ${c.status === "coming_soon" ? "text-soda border-soda" : ""}`}
              data-testid={`filter-${c.slug}`}>
              {c.name}{c.status === "coming_soon" && " · Coming soon"}
            </button>
          ))}
        </div>
      </section>

      {activeCatObj && (
        <section className="px-6 md:px-12 lg:px-16 pt-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            {activeCatObj.image_url && (
              <div className="md:col-span-3">
                <div className="aspect-[4/3] border border-ink overflow-hidden">
                  <img src={resolveImage(activeCatObj.image_url)} alt={activeCatObj.name} className="w-full h-full object-cover" />
                </div>
              </div>
            )}
            <div className={activeCatObj.image_url ? "md:col-span-9" : "md:col-span-12"}>
              <div className="overline text-black/50 mb-2">{activeCatObj.section === "imported" ? "Imported" : "Category"}</div>
              <h2 className="font-display text-4xl md:text-6xl uppercase leading-none flex items-center gap-4 flex-wrap">
                {activeCatObj.name}
                {activeCatObj.status === "coming_soon" && <span className="text-sm md:text-base bg-soda text-paper px-3 py-1 font-bold tracking-widest" data-testid="category-coming-soon">COMING SOON</span>}
              </h2>
              {activeCatObj.description && <p className="mt-3 text-lg font-editorial italic max-w-2xl">{activeCatObj.description}</p>}
            </div>
          </div>
        </section>
      )}

      {/* Brand quick-filter when a category is active */}
      {activeCat !== "all" && (() => {
        const catBrandNames = Array.from(new Set(items.filter(p => p.category_slug === activeCat).map(p => p.brand).filter(Boolean)));
        if (catBrandNames.length === 0) return null;
        return (
          <section className="px-6 md:px-12 lg:px-16 pt-6">
            <div className="overline text-black/50 mb-3">Brand</div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setParam("brand", "all")} className={`chip ${activeBrand === "all" ? "bg-ink text-paper" : ""}`}>All brands</button>
              {catBrandNames.map(b => (
                <button key={b} onClick={() => setParam("brand", b)} className={`chip ${activeBrand === b ? "bg-ink text-paper" : ""}`} data-testid={`brand-filter-${b}`}>{b}</button>
              ))}
            </div>
          </section>
        );
      })()}

      <section className="px-6 md:px-12 lg:px-16 py-10 md:py-16">
        {loadingItems && items.length === 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="catalog-loading" aria-label="Loading catalog">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="border border-ink bg-paper animate-pulse">
                <div className="aspect-square bg-black/5 border-b border-ink" />
                <div className="p-3 md:p-4 space-y-2">
                  <div className="h-2 w-1/3 bg-black/10" />
                  <div className="h-3 w-3/4 bg-black/15" />
                  <div className="h-2 w-1/2 bg-black/10" />
                </div>
              </div>
            ))}
          </div>
        ) : itemsError ? (
          <div className="border border-soda p-10 md:p-16 text-center" data-testid="catalog-error">
            <div className="font-display text-3xl md:text-4xl uppercase mb-3 text-soda">Couldn't load the catalog</div>
            <p className="text-black/60 mb-6">Please check your connection and try again.</p>
            <button onClick={reloadItems} className="btn-primary" data-testid="catalog-retry">Retry</button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-ink p-10 md:p-16 text-center" data-testid="catalog-empty">
            <div className="font-display text-3xl md:text-4xl uppercase mb-4">
              {activeCatObj?.status === "coming_soon" ? "Coming soon" : "No products yet"}
            </div>
            <p className="text-black/60 mb-6">
              {activeCatObj?.status === "coming_soon"
                ? "This category is on its way. Send us an enquiry and we'll notify you when it lands."
                : "We're updating this section. In the meantime, send us an enquiry."}
            </p>
            {settings?.enquiry_form_enabled && <Link to="/enquiry" className="btn-primary">Request pricing →</Link>}
          </div>
        ) : showBrandGroups ? (
          <div className="space-y-14" data-testid="catalog-grid">
            {groupedByBrand.map(({ key, brand, products }) => (
              <div key={key} data-testid={`brand-group-${key}`}>
                <div className="flex items-center gap-4 mb-6 border-b border-black/10 pb-4">
                  {brand?.image_url && (
                    <div className="w-14 h-14 border border-ink overflow-hidden shrink-0 bg-paper">
                      <img src={resolveImage(brand.image_url)} alt={brand.name} className="w-full h-full object-contain" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-display text-2xl md:text-4xl uppercase leading-none flex items-center gap-3 flex-wrap">
                      {key === "__nobrand__" ? "Other" : key}
                      {brand?.status === "coming_soon" && <span className="text-xs bg-soda text-paper px-2 py-0.5 font-bold tracking-widest">COMING SOON</span>}
                    </h3>
                    {brand?.description && <p className="text-sm text-black/60 mt-1 font-editorial italic">{brand.description}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {products.map((p, i) => <ProductCard key={p.id} p={p} i={i} catName={catBySlug[p.category_slug]?.name || p.category_slug} canOrder={canOrder} onAdd={onAdd} />)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="catalog-grid">
            {filtered.map((p, i) => <ProductCard key={p.id} p={p} i={i} catName={catBySlug[p.category_slug]?.name || p.category_slug} canOrder={canOrder} onAdd={onAdd} />)}
          </div>
        )}
      </section>

      <section className="bg-ink text-paper px-6 md:px-12 lg:px-16 py-20 md:py-32 border-t border-ink" data-testid="catalog-cta">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <div className="overline text-white/50 mb-6">Ready to stock up?</div>
            <h2 className="font-display text-4xl md:text-6xl lg:text-8xl uppercase leading-[0.85] tracking-tighter">Send an <span className="text-soda">enquiry.</span></h2>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row gap-3">
            {settings?.enquiry_form_enabled && <Link to="/enquiry" className="btn-primary" style={{ background: "#0047FF", borderColor: "#0047FF" }} data-testid="catalog-cta-enquiry">Request pricing →</Link>}
            <Link to="/contact" className="btn-ghost" style={{ color: "#F4F4F0", borderColor: "#F4F4F0" }} data-testid="catalog-cta-contact">Contact us</Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
